import * as React from "react";
import { Box, Container, Stack, Typography, Button, Chip, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PropertyCard from "../properties/PropertyCard";
import FeaturedPropertiesCarousel from "./FeaturedPropertiesCarousel";
import { useI18n } from "../../i18n/I18nProvider";

type Props = {
  locale: string;
  latest: any[];
  hot: any[];
  sale: any[];
  loading?: boolean;
};

const TYPES = [
  { key: "stonehouse" },
  { key: "studio" },
  { key: "apartment" },
  { key: "maisonette" },
  { key: "house" },
];


export default function HomeClient({
  locale,
  latest = [],
  hot = [],
  sale = [],
  loading = false,
}: Props) {
  const navigate = useNavigate();
  const { t } = useI18n();

  const goType = (type: string) => {
    navigate(`/${locale}/properties?property_type=${encodeURIComponent(type)}`);
  };

  const goStatus = (s: "rent" | "sale") => {
    navigate(`/${locale}/properties?status=${s}`);
  };

  const goAll = () => navigate(`/${locale}/properties`);

  return (
    <Box>
      {/* HERO */}
      <Box sx={{ py: { xs: 5, md: 8 }, borderBottom: "1px solid", borderColor: "divider" }}>
        <Container maxWidth="lg">
          <Stack spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: -0.4 }}>
                {t("brand_name")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: { xs: "none", md: "block" },
                  mt: 0.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("brand_tagline")}
              </Typography>
            </Box>

            <Typography variant="h6" color="text.secondary">
              {t("home.heroSubtitle")}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
              <Button variant="contained" onClick={() => goStatus("rent")} sx={{ borderRadius: 999 }}>
                {t("home.ctaRent")}
              </Button>
              <Button variant="outlined" onClick={() => goStatus("sale")} sx={{ borderRadius: 999 }}>
                {t("home.ctaSale")}
              </Button>
              <Button variant="text" onClick={goAll}>
                {t("home.ctaAllProperties")}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 4 }, mb: 8 }}>
        {/* FEATURED */}
        <Stack spacing={1.2} sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.4 }}>
            {t("home.featuredTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("home.featuredSubtitle")}
          </Typography>
        </Stack>

        {loading || !latest?.length ? (
          <Skeleton variant="rounded" height={420} />
        ) : (
          <FeaturedPropertiesCarousel
            key={`featured-${latest.length}-${latest.map((x) => x.id).join("-")}`}
            locale={locale}
            items={latest}
          />
        )}

        {/* PROPERTY TYPE chips */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            {t("home.browseByTypeTitle")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {TYPES.map((it) => (
              <Chip
                key={it.key}
                label={t(`propertyTypes.${it.key}`)}
                clickable
                onClick={() => goType(it.key)}
                sx={{ borderRadius: 999, px: 1 }}
              />
            ))}
          </Stack>
        </Box>

        {/* HOT OFFERS */}
        <Box sx={{ mt: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {t("home.hotOffersTitle")}
            </Typography>
            <Button onClick={() => navigate(`/${locale}/properties?label=${encodeURIComponent("Hot Offer")}`)}>
              {t("home.viewAllOffers")}
            </Button>
          </Stack>

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" } }}>
            {(hot || []).map((p) => (
              <PropertyCard key={p.id} locale={locale} property={p} />
            ))}
          </Box>
        </Box>

        {/* SALE */}
        <Box sx={{ mt: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {t("home.saleTitle")}
            </Typography>
            <Button onClick={() => goStatus("sale")}>{t("home.viewAllSales")}</Button>
          </Stack>

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" } }}>
            {(sale || []).map((p) => (
              <PropertyCard key={p.id} locale={locale} property={p} />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
